import { useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { API_BASE_URL } from '../services/api';
import { ENTITY_DEPENDENCIES } from './useApi';
import type { LiveEvent } from '../types';

export type LiveStatus = 'connecting' | 'online' | 'offline';

/**
 * Subscribes to the server-sent event stream and refreshes cached data when
 * another user changes something. Returns the connection status.
 */
export function useLiveUpdates(enabled: boolean): LiveStatus {
  const queryClient = useQueryClient();
  const [status, setStatus] = useState<LiveStatus>('connecting');
  const pending = useRef<Set<string>>(new Set());
  const flushTimer = useRef<number>();

  useEffect(() => {
    if (!enabled) return;

    const source = new EventSource(`${API_BASE_URL}/events`, { withCredentials: true });
    let heartbeatTimer: number | undefined;

    const markAlive = () => {
      setStatus('online');
      window.clearTimeout(heartbeatTimer);
      // Server sends a heartbeat every 15 s; treat 40 s of silence as a lost connection
      heartbeatTimer = window.setTimeout(() => setStatus('offline'), 40_000);
    };

    source.onopen = markAlive;
    source.onerror = () => setStatus('offline');
    source.onmessage = (message) => {
      markAlive();
      let event: LiveEvent;
      try {
        event = JSON.parse(message.data);
      } catch {
        return;
      }
      if (event.type !== 'changed' || !event.entity) return;

      for (const key of ENTITY_DEPENDENCIES[event.entity] ?? [event.entity]) pending.current.add(key);
      // Batch bursts of events (e.g. a GPS import) into one refetch
      window.clearTimeout(flushTimer.current);
      flushTimer.current = window.setTimeout(() => {
        pending.current.forEach((key) => queryClient.invalidateQueries({ queryKey: [key] }));
        pending.current.clear();
      }, 300);
    };

    return () => {
      source.close();
      window.clearTimeout(heartbeatTimer);
      window.clearTimeout(flushTimer.current);
    };
  }, [enabled, queryClient]);

  return status;
}
